import PropTypes from 'prop-types'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import ActionSubmittedModal from '../../Modal/ActionSubmittedModal'
import RosterLabels from '../RosterLabels'
import StudentRow from './StudentRow'
import * as eventActions from '../../../../actions/eventActions'
import { UncontrolledPopover } from 'reactstrap'
import colors from '../../../../assets/styles/globals.scss'
import styles from './EventRoster.module.scss'
import { ReactComponent as GolferIcon } from '../../../../assets/images/common/icons/golfer-half-icon.svg'

import classNames from 'classnames'
import Tooltip from '../../../common/Tooltip'
import SubmitButton from '../../../common/buttons/SubmitButton'

const EventRoster = ({ removeStudent }) => {
  const dispatch = useDispatch()
  const event = useSelector(state => state.nineHoleEvent)
  const { t } = useTranslation()

  const [selectedStudent, setSelectedStudent] = useState(null)
  const [showSentReminderModal, setShowSentReminderModal] = useState(false)

  const CloseReminderModalButton = (
    <SubmitButton
      onClick={() => setShowSentReminderModal(false)}
      buttonMessage={t('common.close')}
      buttonColor='navy-button'
    />
  )

  const handleStudentMenuClick = (student) => {
    setSelectedStudent(student)
  }

  const sendRSVP = () => {
    dispatch(eventActions.sendEventReminder({ eventId: event.id, userId: selectedStudent.id })).unwrap()
      .then(() => setShowSentReminderModal(true))
      .catch(() => {
        toast.error(t('events.roster.rsvpReminderFailed', { name: selectedStudent.firstName + ' ' + selectedStudent.lastName }),
          { position: toast.POSITION.BOTTOM_LEFT })
      })
  }

  const handleRSVPToggle = () => {
    dispatch(eventActions.updateRsvpStatus({ id: event.id, rsvpStatus: event.rsvpStatus }))
  }

  return (
    <div className='d-flex flex-column table-container'>
      <RosterLabels handleRSVPToggle={handleRSVPToggle}/>
      <div className={classNames('d-flex flex-column', styles.enrollmentsList)}>
        <Tooltip/>
        {event.students.length > 0 ?
          <table className='table animated fadeIn w-100'>
            <thead>
              <tr>
                <th className='golfer-header'>{t('events.common.golfer')}</th>
                <th className='px-5'>{t('events.roster.ageGender')}</th>
                <th>{t('events.roster.program')}</th>
                <th>{t('events.roster.rsvpLabel')}</th>
                <th>{t('events.roster.pairingStatus')}</th>
                <th>{t('events.roster.note')}</th>
                <th className='centered-text'>{t('common.golf.recordScore')}</th>
                <th className='centered-text'>{t('events.roster.levelAttempts')}</th>
                <th className='px-5 centered-text'>{status !== 'completed' && t('events.roster.actions')}</th>
              </tr>
            </thead>
            <tbody>
              { event.students.map((std) =>
                <StudentRow key={std.id} std={std} handleStudentMenuClick={handleStudentMenuClick} event={event}/>
              )}

              <UncontrolledPopover
                placement='bottom'
                target='studentMenuButton'
                trigger='legacy'
              >
                <div className={classNames('d-flex flex-column', styles.studentActions)}>
                  <p aria-hidden='true' onClick={() => removeStudent(selectedStudent.enrollmentId)}>{t('events.roster.removeGolfer')}</p>
                  <p aria-hidden='true' onClick={sendRSVP}>{t('events.roster.sendRsvpReminder')}</p>
                </div>
              </UncontrolledPopover>
            </tbody>
          </table>
          :
          <div className={styles.noStudentsPlaceholder}>
            <GolferIcon id='golferIcon' fill={colors.lightNavy} width={'64px'} height={'64px'}/>
            <p className={styles.placeholderTitle}>{t('events.roster.noStudentsEnrolled')}</p>
            <div className={styles.placeholderDescription}>{t('events.roster.noEnrollments')}</div>
          </div>
        }
      </div>
      <ActionSubmittedModal
        showModal={showSentReminderModal}
        closeModal={() => setShowSentReminderModal(false)}
        firstButton={CloseReminderModalButton}
        title={t('events.roster.rsvpReminderSent')}
        icon={<i className='fa fa-envelope'/>}
      />
    </div>
  )
}

EventRoster.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number,
    students: PropTypes.array,
    status: PropTypes.string,
    rsvpStatus: PropTypes.string,
  }),
  removeStudent: PropTypes.func.isRequired,
}

export default EventRoster
